import React from 'react';
import type { Control } from 'react-hook-form';
import {
  Eye,
  Heart,
  MessageCircle,
  Navigation,
  Bookmark,
  BarChart3,
} from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { AddPostFormValues } from '../../validators/content.validators';

interface PostMetricsFieldsProps {
  control: Control<AddPostFormValues>;
}

export const PostMetricsFields: React.FC<PostMetricsFieldsProps> = ({ control }) => {
  return (
    <div className="bg-[#FAF9FF] border border-[#E9D5FF]/30 rounded-[16px] p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#A655F8]/5">
          <BarChart3 className="w-4 h-4 stroke-[2]" />
        </div>
        <span className="text-[16px] font-semibold text-slate-800">
          Performance Metrics (optional)
        </span>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <FormField
            control={control}
            name="views"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-[#A655F8]" />
                  Views
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="h-11 bg-white border border-[#E2E8F0] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none rounded-xl text-sm transition-colors"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="likes"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-[#A655F8]" />
                  Likes
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="h-11 bg-white border border-[#E2E8F0] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none rounded-xl text-sm transition-colors"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="comments"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4 text-[#A655F8]" />
                  Comments
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="h-11 bg-white border border-[#E2E8F0] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none rounded-xl text-sm transition-colors"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={control}
            name="shares"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-[#A655F8]" />
                  Shares
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="h-11 bg-white border border-[#E2E8F0] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none rounded-xl text-sm transition-colors"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="saves"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Bookmark className="w-4 h-4 text-[#A655F8]" />
                  Saves
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    className="h-11 bg-white border border-[#E2E8F0] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:outline-none rounded-xl text-sm transition-colors"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
